import { useCallback } from 'react';
import { useAtomValue } from 'jotai/react';
import { contentTransformAtom } from './contentTransformAtom';
import { useContentViewBox } from './useContentViewBox';
import { useViewModelSVGRef } from './ViewModelSVGRefContext';
import { Point } from '@view-model/models/common/basic';

/**
 * クライアント座標（clientX, clientY）をキャンバス座標（ワールド座標）に変換する関数を返すフック。
 *
 * 変換手順:
 * 1. SVG要素の getBoundingClientRect() でページ上の位置を取得し、クライアント座標から SVG ピクセル相対座標を求める
 * 2. ViewBox は中央を原点とする座標系（minX = -width/2）なので、SVG ピクセル相対座標に minX/minY を加算して SVG 座標系に変換
 * 3. D3 の ZoomTransform.invert() を適用してキャンバス座標を得る
 */
export const useClientToCanvasPoint = (): ((clientPoint: Point) => Point | null) => {
  const contentTransform = useAtomValue(contentTransformAtom);
  const contentViewBox = useContentViewBox();
  const svgRef = useViewModelSVGRef();

  return useCallback(
    (clientPoint: Point): Point | null => {
      const svgEl = svgRef?.current;
      // SVG 未マウントまたは ViewBox 未計測時は変換不能として null を返す
      if (!svgEl || !contentViewBox) return null;

      const svgRect = svgEl.getBoundingClientRect();
      // SVG 内のピクセル相対座標
      const svgPixelX = clientPoint.x - svgRect.left;
      const svgPixelY = clientPoint.y - svgRect.top;
      // ViewBox は中央原点なので minX / minY を加算して SVG 座標系に変換
      const svgCoordX = svgPixelX + contentViewBox.minX;
      const svgCoordY = svgPixelY + contentViewBox.minY;
      // D3 ZoomTransform の逆変換でキャンバス座標へ
      const canvasX = contentTransform.invertX(svgCoordX);
      const canvasY = contentTransform.invertY(svgCoordY);
      return new Point(canvasX, canvasY);
    },
    [contentTransform, contentViewBox, svgRef]
  );
};
